import React from "react";
import { Typography, Link, Stack, Chip } from "@mui/material";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../Router/router";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import ContractDetailsCard from "../../Cards/ContractDetailsCard/contract-details-card";
import { thumbnailService } from "../../../Core/Api/services";
import moment from "moment";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ContractAndRightType } from "../../../Core/Api/Right/right-api-service";
import { useTranslation } from "react-i18next";

export interface ContractDetailsPanelProps {
  width?: number;
  open: boolean;
  contract: ContractAndRightType | undefined;
  onClose?: React.MouseEventHandler;
}

const ContractDetailsPanel: React.FC<ContractDetailsPanelProps> = (props) => {
  const { t } = useTranslation();
  const { contract } = props;

  return (
    <Panel
      header={
        <PanelHeader
          title={t("panel:title.contractDetails")}
          onClose={props.onClose as PanelHeaderProps["onClose"]}
          rightContent={
            <Link
              component={RouterLink}
              to={`${routePaths.contractsAndRights.path}/${contract?.contractId}`}
              underline="hover"
              color="primary.text"
            >
              <OpenInFullOutlinedIcon sx={{ mr: 0.5 }} /> {t("common:open")}
            </Link>
          }
        />
      }
      open={props.open}
      onClose={props.onClose}
      reload={contract?.id}
    >
      <Stack direction="row" justifyContent="space-between" pb={2} mb={2} spacing={3}>
        <Stack flex={1}>
          <Typography variant="h6" color="text.placeholder">
            {t("panel:contractPanel.body.contractTitle")}
          </Typography>
          <Typography variant="h3" component="h2">
            {contract?.titleInfos?.name}
          </Typography>
          {contract?.okDelivery && contract?.type !== "Acquisition" && (
            <Chip
              label={t("chip:label.deliveryOk")}
              variant="outlined"
              color="success"
              size="medium"
              icon={<CheckIcon />}
              sx={{ width: 140, mt: 2 }}
            />
          )}
          {!contract?.okDelivery && contract?.type !== "Acquisition" && (
            <Chip
              label={t("chip:label.noDelivery")}
              variant="outlined"
              color="primary"
              size="medium"
              icon={<ClearIcon />}
              sx={{ width: 140, mt: 2 }}
            />
          )}
        </Stack>
        <img
          src={thumbnailService.GetObjectPosterUrl(contract?.titleInfos?.id, ThumbnailSize.small)}
          alt={contract?.titleInfos?.name}
          width={75}
          height={100}
          loading="lazy"
        />
      </Stack>
      <ContractDetailsCard
        contractId={contract?.titleId!}
        contractTitle={contract?.titleInfos?.name ?? ""}
        customerName={contract?.customerName ?? ""}
        broadcaster={contract?.broadcasterName ?? ""}
        startDate={moment(contract?.startDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
        endDate={moment(contract?.endDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
        id={contract?.externalId ?? ""}
        workId={contract?.workRecordId ?? ""}
        externalId={contract?.externalId ?? ""}
        rightType={contract?.type ?? ""}
        languages={contract?.languages.map((v) => v.value) ?? []}
        territories={contract?.territories.map((v) => v.value) ?? []}
        mediaName={contract?.mediaNamesString ?? ""}
        small
        showAllLangsAndTerritories
      />
    </Panel>
  );
};

export default ContractDetailsPanel;
